import { zodResolver } from "@hookform/resolvers/zod";
import { createContext, useContext, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/base/buttons/button";
import { FormTextField } from "@/components/base/form/form-text-field";
import {
  Form,
  FormField,
  type UseHookFormProps,
  useHookForm,
} from "@/components/base/form/hook-form";
import { cx } from "@/utils/cx";
import { HttpStatusCode } from "axios";
import type { TalentTagDto } from "@fp/admin-api-client/types/talentTagDto.js";
import { createTalentTag } from "@fp/admin-api-client/client";

export const TagFormSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
});

export type TagFormSchema = z.infer<typeof TagFormSchema>;

export type TagFormContext = {
  form: UseFormReturn<TagFormSchema>;
  handleSubmit: () => void;
  transition: boolean;
  errorMessage: string | undefined;
};

export const TagFormContext = createContext<TagFormContext | undefined>(
  undefined,
);

export const useTagForm = () => {
  const context = useContext(TagFormContext);
  if (!context) {
    throw new Error("TagFormContext not found");
  }
  return context;
};

export type TagFormProps = UseHookFormProps<TagFormSchema, TalentTagDto> & {
  tag: TalentTagDto | null;
  children: ((context: TagFormContext) => React.ReactNode) | React.ReactNode;
};

// API call using the actual createTag function
export const TagFormSubmitHandler = async (formValues: TagFormSchema) => {
  try {
    if (formValues.id) {
      // TODO: Replace with actual updateTag API call when available
      // const result = await updateTag(formValues.id, formValues);

      // For now, simulate success for updates
      await new Promise((resolve) => setTimeout(resolve, 500));

      return {
        success: true as const,
        data: {
          id: formValues.id,
          name: formValues.name,
          description: formValues.description || null,
          hexColor: null,
          isActive: true,
          createdDate: new Date().toISOString(),
          modifiedDate: new Date().toISOString(),
        } satisfies TalentTagDto,
      };
    } else {
      // Use the actual createTalentTag API for new tags
      const result = await createTalentTag({
        name: formValues.name,
        description: formValues.description || null,
        hexColor: null,
        isActive: true,
      });

      if (
        result.status !== HttpStatusCode.Ok &&
        result.status !== HttpStatusCode.Created
      ) {
        return {
          success: false as const,
          error: new Error("Unable to create tag"),
        };
      }
      return { success: true as const, data: result.data };
    }
  } catch (error) {
    const message =
      (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ?? "Unable to save tag";
    return {
      success: false as const,
      error: new Error(message),
    };
  }
};

export const TagForm = ({
  tag,
  children,
  onSubmit = TagFormSubmitHandler,
  ...props
}: TagFormProps) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  const defaultValues = useMemo(
    () => ({
      id: tag?.id,
      name: tag?.name || "",
      description: tag?.description || "",
    }),
    [tag],
  );

  const { form, handleSubmit, transition } = useHookForm<
    TagFormSchema,
    TalentTagDto
  >({
    resolver: zodResolver(TagFormSchema),
    defaultValues,
    onSuccess: async (data) => {
      setErrorMessage(undefined);
      await props.onSuccess?.(data);
    },
    onError: async (error) => {
      setErrorMessage(
        error instanceof Error ? error.message : "Unable to save tag",
      );
      await props.onError?.(error);
    },
    onSubmit,
    ...props,
  });

  const context = useMemo(
    () => ({ form, handleSubmit, transition, errorMessage }),
    [form, handleSubmit, transition, errorMessage],
  );

  return (
    <TagFormContext.Provider value={context}>
      <Form {...form}>
        {typeof children === "function" ? children(context) : children}
      </Form>
    </TagFormContext.Provider>
  );
};

export const TagFormErrorMessage = () => {
  const { errorMessage } = useTagForm();

  if (!errorMessage) return null;

  return <p className="text-error-primary">{errorMessage}</p>;
};

export const TagFormContent = ({ className }: { className?: string }) => {
  const { form } = useTagForm();

  return (
    <div className={cx("flex flex-col gap-6 mb-4", className)}>
      <div className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormTextField
              field={field}
              label="Name"
              placeholder="Enter tag name"
              className="w-full"
            />
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormTextField
              field={field}
              label="Description"
              placeholder="Enter tag description (optional)"
              className="w-full"
            />
          )}
        />
      </div>
    </div>
  );
};

export const TagFormActions = ({ className }: { className?: string }) => {
  const { handleSubmit, transition } = useTagForm();

  return (
    <div className={cx("flex flex-col gap-4", className)}>
      <Button
        type="submit"
        size="lg"
        color="primary"
        className="w-full max-w-lg mx-auto"
        isDisabled={transition}
        onClick={handleSubmit}
      >
        {transition ? "Saving..." : "Save Tag"}
      </Button>
    </div>
  );
};
